import type { CSSProperties } from 'react';

// The sixth renderer, in HTML.
// It consumes the same Document IR the PDF/Word/Excel/PowerPoint/CSV/JSON
// renderers do, returned verbatim by POST /api/reports/preview.

// ── Types for the Document IR ───────────────────────────────────────────────

interface Figure {
  label: string;
  value: number | string | boolean;
  unit?: string;
  tone?: 'good' | 'bad' | 'warn' | 'neutral';
  anomaly?: boolean;
  delta?: {
    prior: number | string;
    change: number;
    pct?: number;
  };
}

interface ChartPoint {
  label: string;
  value: number;
}

interface ChartSeries {
  name?: string;
  points: ChartPoint[];
}

interface ChartDef {
  alt: string;
  axes?: { x?: string; y?: string };
  series?: ChartSeries[];
}

interface TableCol {
  key: string;
  label: string;
  align?: 'right';
  kind?: string;
}

interface TableRow {
  [key: string]: string | number | boolean | null;
  tone?: string;
}

interface TableDef {
  cols: TableCol[];
  rows: TableRow[];
  totals: Record<string, string | number | boolean | null> | null;
}

interface SectionData {
  key: string;
  title: string;
  empty?: boolean;
  figures: Figure[];
  chart?: ChartDef;
  table?: TableDef;
  notes: string[];
  note?: string;
}

interface ReportMeta {
  period: { label: string; from: string; to: string };
  typeLabel: string;
  scope: string;
  comparePeriod?: { label: string };
  branding?: {
    marking?: string;
    contractNumber?: string;
    cdrl?: string;
    agency?: string;
    program?: string;
    coName?: string;
    corName?: string;
    distributionStatement?: string;
    scopeNote?: string;
  };
  generatedAt: string;
  generatedBy: string;
  dataAsOf: string;
}

interface Anomaly {
  sectionKey: string;
  label: string;
  text: string;
}

interface Narrative {
  text: string;
  attribution?: string;
}

export interface ReportDoc {
  meta: ReportMeta;
  sections: SectionData[];
  anomalies: Anomaly[];
  notes: string[];
  narrative?: Narrative;
}

// ── Formatting helpers ──────────────────────────────────────────────────────

const EMPTY_NOTE = 'No data for this period.';
const REVIEWER_NOTE_PREFIX = 'Reviewer note:';

function fmt(v: number | string | boolean | null | undefined, kind?: string): string {
  if (v == null || v === '') return '—';
  if (kind === 'money') return '$' + Math.round(Number(v) || 0).toLocaleString('en-US');
  if (kind === 'number') {
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString('en-US') : String(v);
  }
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function fmtValue(value: number | string | boolean, unit?: string): string {
  if (unit === 'USD') return fmt(value, 'money');
  if (unit === '%') return `${fmt(value, 'number')}%`;
  if (unit) return `${fmt(value, 'number')} ${unit}`;
  return fmt(value, 'number');
}

const fmtFigure = (f: Figure) => fmtValue(f.value, f.unit);

function fmtDelta(f: Figure): string | null {
  const d = f && f.delta;
  if (!d || typeof d !== 'object' || !Number.isFinite(d.change)) return null;
  const base = fmtValue(d.prior, f.unit);
  if (d.change === 0) return `no change vs ${base}`;
  const magnitude = (d.change > 0 ? '+' : '-') + fmtValue(Math.abs(d.change), f.unit);
  const pct = d.pct == null ? 'from zero' : `${d.pct > 0 ? '+' : ''}${d.pct}%`;
  return `${magnitude} (${pct}) vs ${base}`;
}

const PREVIEW_MAX_ROWS = 100;

function chartRows(chart: ChartDef) {
  const pts = (chart && chart.series && chart.series[0] && chart.series[0].points) || [];
  return {
    cols: [
      { key: 'label', label: (chart.axes && chart.axes.x) || 'Category' },
      {
        key: 'value',
        label: (chart.series && chart.series[0] && chart.series[0].name) || 'Value',
        align: 'right' as const,
        kind: 'number',
      },
    ],
    rows: pts.map((p) => ({ label: String(p.label), value: Number(p.value) || 0 })),
    totals: null,
  };
}

// ── Sub-components ──────────────────────────────────────────────────────────

function Figures({ figures }: { figures: Figure[] }) {
  if (!figures.length) return null;
  return (
    <div className="rpt-figs">
      {figures.map((f, n) => {
        const delta = fmtDelta(f);
        const lead = n === 0;
        return (
          <div
            className={'rpt-fig' + (lead ? ' rpt-fig-lead' : '') + (f.anomaly ? ' rpt-fig-flag' : '')}
            key={f.label}
          >
            <div className="rpt-fig-l">
              {f.label}
              {f.anomaly && (
                <span className="rpt-flag" title="Listed under Attention">
                  Moved
                </span>
              )}
            </div>
            <div
              className={
                'rpt-fig-v' + (f.tone && f.tone !== 'neutral' ? ' rpt-tone-' + f.tone : '')
              }
            >
              {fmtFigure(f)}
            </div>
            {delta && <div className="rpt-fig-d">{delta}</div>}
          </div>
        );
      })}
    </div>
  );
}

function Rows({ table }: { table: TableDef | undefined }) {
  if (!table || !table.rows.length) return null;
  const shown = table.rows.slice(0, PREVIEW_MAX_ROWS);
  const dropped = table.rows.length - shown.length;
  return (
    <>
      <div className="rpt-tablewrap">
        <table className="rpt-table">
          <thead>
            <tr>
              {table.cols.map((c) => (
                <th key={c.key} className={c.align === 'right' ? 'rpt-r' : undefined} scope="col">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, n) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={n} className={r.tone ? `rpt-row-${r.tone}` : undefined}>
                {table.cols.map((c) => (
                  <td key={c.key} className={c.align === 'right' ? 'rpt-r' : undefined}>
                    {fmt(r[c.key] as string | number | boolean | null, c.kind)}
                  </td>
                ))}
              </tr>
            ))}
            {table.totals ? (
              <tr className="rpt-total">
                {table.cols.map((c) => (
                  <td key={c.key} className={c.align === 'right' ? 'rpt-r' : undefined}>
                    {fmt(table.totals[c.key] as string | number | boolean | null, c.kind)}
                  </td>
                ))}
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {dropped > 0 && (
        <p className="rpt-note">
          +{dropped.toLocaleString('en-US')} more rows — the download carries them all
        </p>
      )}
    </>
  );
}

const BAR_TONE_WORDS: Record<string, string[]> = {
  good: ['complete', 'done', 'green', 'on-track', 'on track'],
  warn: ['amber', 'at-risk', 'at risk'],
  bad: ['overdue', 'red', 'breached', 'blocked', 'critical'],
};

function toneForLabel(label: string): string | null {
  const l = String(label).trim().toLowerCase();
  for (const tone of Object.keys(BAR_TONE_WORDS)) {
    if (BAR_TONE_WORDS[tone].includes(l)) return tone;
  }
  return null;
}

function barValueLabel(v: number, axes?: { x?: string; y?: string }): string {
  const n = Number(v) || 0;
  return axes && axes.y === 'USD' ? fmtValue(n, 'USD') : n.toLocaleString('en-US');
}

function planByMonth(table?: TableDef): Record<string, number> | null {
  if (!table || !table.rows) return null;
  const hasPlanned = table.cols && table.cols.some((c) => c.key === 'planned');
  if (!hasPlanned) return null;
  const map: Record<string, number> = {};
  for (const r of table.rows) {
    if (r && r.month != null && r.planned != null) map[String(r.month)] = Number(r.planned);
  }
  return map;
}

function Chart({ chart, byMonth }: { chart: ChartDef; byMonth?: Record<string, number> | null }) {
  const pts = (chart.series && chart.series[0] && chart.series[0].points) || [];
  if (!pts.length) return <p className="rpt-empty">{EMPTY_NOTE}</p>;
  const max = Math.max(...pts.map((p) => Number(p.value) || 0), 1);
  const axisLabel = chart.axes && chart.axes.y;
  return (
    <>
      <div className="rpt-chart-wrap">
        {axisLabel && <div className="rpt-chart-axis">{axisLabel}</div>}
        <div className="rpt-chart" role="img" aria-label={chart.alt}>
          <div className="rpt-chart-grid" aria-hidden="true">
            <span className="rpt-grid-l" style={{ bottom: '33%' } as CSSProperties} />
            <span className="rpt-grid-l" style={{ bottom: '66%' } as CSSProperties} />
          </div>
          {pts.map((p) => {
            const v = Number(p.value) || 0;
            const plan = byMonth ? byMonth[String(p.label)] : null;
            const overPlan = plan != null && v > plan;
            const tone = plan != null ? (overPlan ? 'bad' : 'good') : toneForLabel(p.label);
            return (
              <div
                className="rpt-bar-col"
                key={String(p.label)}
                title={plan != null ? (overPlan ? 'Over plan' : 'At or under plan') : undefined}
              >
                <div className="rpt-bar-v">
                  {barValueLabel(v, chart.axes)}
                  {plan != null && (
                    <span
                      className={'rpt-bar-flag rpt-bar-flag-' + (overPlan ? 'bad' : 'good')}
                      aria-hidden="true"
                    >
                      {overPlan ? ' ▲' : ' ▼'}
                    </span>
                  )}
                </div>
                <div
                  className={'rpt-bar' + (tone ? ` rpt-bar-${tone}` : '')}
                  style={{ height: `${Math.max(2, (v / max) * 100)}%` } as CSSProperties}
                />
                <div className="rpt-bar-l">{String(p.label)}</div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="rpt-note rpt-alt">{chart.alt}</p>
    </>
  );
}

const MILESTONE_TONE: Record<string, string> = {
  complete: 'good',
  overdue: 'bad',
  'in progress': 'ip',
  pending: 'pd',
};

function ProgressChart({ chart }: { chart: ChartDef }) {
  const pts = (chart.series && chart.series[0] && chart.series[0].points) || [];
  if (!pts.length) return <p className="rpt-empty">{EMPTY_NOTE}</p>;
  const total = pts.reduce((s, p) => s + (Number(p.value) || 0), 0);
  const done = pts.reduce(
    (s, p) => (String(p.label).toLowerCase() === 'complete' ? s + (Number(p.value) || 0) : s),
    0
  );
  const gap = total - done;
  return (
    <>
      <div className="rpt-mstat" role="img" aria-label={chart.alt}>
        <div className="rpt-mstat-head">
          {total > 0 && gap === 0 ? (
            <span className="rpt-mstat-gap rpt-mstat-gap-good">
              All {total.toLocaleString('en-US')} complete
            </span>
          ) : (
            <>
              <span className="rpt-mstat-gap">{gap.toLocaleString('en-US')}</span>
              <span className="rpt-mstat-gap-l">
                of {total.toLocaleString('en-US')} not yet complete
              </span>
            </>
          )}
        </div>
        <div className="rpt-mstat-track">
          <div className="rpt-mstat-fill">
            {pts.map((p) => {
              const v = Number(p.value) || 0;
              const pct = total > 0 ? (v / total) * 100 : 0;
              const tone = MILESTONE_TONE[String(p.label).toLowerCase()] || 'pd';
              return pct > 0 ? (
                <div
                  key={String(p.label)}
                  className={`rpt-mstat-seg rpt-mstat-${tone}`}
                  style={{ width: `${pct}%` } as CSSProperties}
                />
              ) : null;
            })}
          </div>
          <div className="rpt-mstat-target" aria-hidden="true">
            <span className="rpt-mstat-target-l">Target — all complete</span>
          </div>
        </div>
        <div className="rpt-mstat-legend">
          {pts.map((p) => {
            const v = Number(p.value) || 0;
            const tone = MILESTONE_TONE[String(p.label).toLowerCase()] || 'pd';
            return (
              <span className="rpt-mstat-lg" key={String(p.label)}>
                <i className={`rpt-mstat-dot rpt-mstat-${tone}`} aria-hidden="true" />
                {String(p.label)}
                <b>{v.toLocaleString('en-US')}</b>
              </span>
            );
          })}
        </div>
      </div>
      <p className="rpt-note rpt-alt">{chart.alt}</p>
    </>
  );
}

function Section({ section }: { section: SectionData }) {
  const table =
    section.table && section.table.rows.length
      ? section.table
      : section.chart
        ? chartRows(section.chart)
        : null;
  return (
    <section className="rpt-section" aria-labelledby={'rpt-h-' + section.key}>
      <h3 className="rpt-h" id={'rpt-h-' + section.key}>
        {section.title}
      </h3>
      {section.empty ? (
        <p className="rpt-empty">{EMPTY_NOTE}</p>
      ) : (
        <>
          <Figures figures={section.figures} />
          {section.chart && section.key === 'progress' && <ProgressChart chart={section.chart} />}
          {section.chart && section.key === 'burn' && (
            <Chart chart={section.chart} byMonth={planByMonth(section.table)} />
          )}
          {section.chart && section.key !== 'progress' && section.key !== 'burn' && (
            <Chart chart={section.chart} />
          )}
          <Rows table={table} />
          {section.notes.map((n) => (
            <p className="rpt-note" key={n}>
              {n}
            </p>
          ))}
        </>
      )}
      {section.note && (
        <p className="rpt-note rpt-reviewer">
          <strong>{REVIEWER_NOTE_PREFIX}</strong> {section.note}
        </p>
      )}
    </section>
  );
}

function brandingLines(b?: ReportMeta['branding']): string[] {
  if (!b) return [];
  const pair = (k: string, v?: string) => (v ? [`${k}: ${v}`] : []);
  return [
    ...pair('Contract', b.contractNumber),
    ...pair('CDRL / DID', b.cdrl),
    ...pair('Agency', b.agency),
    ...pair('Program', b.program),
    ...pair('Contracting Officer', b.coName),
    ...pair('COR', b.corName),
    ...(b.distributionStatement ? [b.distributionStatement] : []),
  ];
}

export function ReportPreview({ report }: { report: ReportDoc | null }) {
  if (!report) return null;
  const m = report.meta;
  const marking = (m.branding && m.branding.marking) || null;
  const brand = brandingLines(m.branding);
  const anomalies = report.anomalies || [];
  const notes = report.notes || [];

  return (
    <article className="rpt-doc">
      {marking && <div className="rpt-marking">{marking}</div>}

      <header className="rpt-head">
        <div className="rpt-kicker">{m.period.label}</div>
        <h2 className="rpt-title">{m.typeLabel}</h2>
        <div className="rpt-sub">
          {m.scope} · {m.period.from} to {m.period.to}
        </div>
        {m.comparePeriod && <div className="rpt-sub">Compared against {m.comparePeriod.label}</div>}
        {brand.length > 0 && (
          <div className="rpt-brand">
            {brand.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </div>
        )}
        {notes.map((n) => (
          <p key={n} className="rpt-note">
            {n}
          </p>
        ))}
      </header>

      {report.sections.length >= 3 && (
        <nav className="rpt-toc" aria-label="Contents">
          <span className="rpt-label">Contents</span>
          <ul>
            {report.sections.map((s) => (
              <li key={s.key}>
                <a href={'#rpt-h-' + s.key}>{s.title}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {anomalies.length > 0 && (
        <section className="rpt-attention" aria-label="Attention">
          <h3 className="rpt-h">Attention</h3>
          <ul>
            {anomalies.map((a) => (
              <li key={a.sectionKey + a.label}>{a.text}</li>
            ))}
          </ul>
        </section>
      )}

      {report.narrative && report.narrative.text && (
        <section className="rpt-narrative" aria-label="Narrative">
          <h3 className="rpt-h">Narrative</h3>
          <p>{report.narrative.text}</p>
          {report.narrative.attribution && (
            <p className="rpt-note rpt-attrib">{report.narrative.attribution}</p>
          )}
        </section>
      )}

      {report.sections.map((s) => (
        <Section section={s} key={s.key} />
      ))}

      <footer className="rpt-prov">
        Generated {m.generatedAt} · {m.generatedBy} · {m.scope} · data as of {m.dataAsOf}
      </footer>
      {marking && <div className="rpt-marking">{marking}</div>}
    </article>
  );
}
